/**
 * WebMCP (https://webmachinelearning.github.io/webmcp/) — exposes a few AMZScout
 * actions to an AI agent driving the browser. Registered on client entry, so the
 * tools are available as soon as any page loads.
 */

const SITE_URL = process.env.SITE_URL || "https://amzscout.net";

const PAGES = [
  {
    id: "home",
    title: "AMZScout home",
    path: "/",
    description: "Overview of the AMZScout tools for Amazon sellers.",
  },
  {
    id: "pro-ai",
    title: "AMZScout PRO AI Extension",
    path: "/pro-ai/",
    description:
      "Chrome extension with AI-powered product research on any Amazon page.",
  },
  {
    id: "product-database",
    title: "Product Database",
    path: "/product-database/",
    description:
      "Search over 600M Amazon products by filters to find profitable ones.",
  },
  {
    id: "keyword-tools",
    title: "Amazon keyword tools",
    path: "/amazon-keyword-tools/",
    description:
      "Keyword research, reverse ASIN lookup and listing optimization tools.",
  },
  {
    id: "sellers-bundle",
    title: "Amazon Sellers Bundle",
    path: "/amazon-sellers-bundle/",
    description: "All AMZScout tools bundled: extensions plus the web app.",
  },
  {
    id: "online-arbitrage",
    title: "Online Arbitrage extension",
    path: "/online-arbitrage/",
    description:
      "Buy Box analysis and ROI calculations for resellers and dropshippers.",
  },
  {
    id: "pricing",
    title: "Pricing",
    path: "/pricing/",
    description: "AMZScout subscription plans and prices.",
  },
  {
    id: "ai-integration",
    title: "AMZScout AI Skill + MCP",
    path: "/.well-known/mcp/server-card.json",
    description: "Machine-readable card for the hosted AMZScout MCP server.",
  },
];

const AI_INTEGRATION = {
  mcpEndpoint: "https://chatbot.amzscout.net/mcp",
  transport: "streamable-http",
  legacySseEndpoint: "https://chatbot.amzscout.net/mcp/sse",
  httpApi: "https://chatbot.amzscout.net/openapi.json",
  authentication:
    "Authorization: Bearer <AMZScout API key>, or OAuth 2.1 — see /auth.md",
  apiKeyUrl: "https://amzscout.net/amazon-product-mcp-for-ai-agents/",
  serverCard: `${SITE_URL}/.well-known/mcp/server-card.json`,
  skillsIndex: `${SITE_URL}/.well-known/agent-skills/index.json`,
};

const asText = (payload) => ({
  content: [{ type: "text", text: JSON.stringify(payload, null, 2) }],
});

const TOOLS = [
  {
    name: "amzscout_list_pages",
    description:
      "List the main AMZScout pages (tools, bundles, pricing) with their URLs and a short description of each.",
    inputSchema: {
      type: "object",
      properties: {},
      additionalProperties: false,
    },
    execute: async () =>
      asText(
        PAGES.map(({ id, title, path, description }) => ({
          id,
          title,
          description,
          url: `${SITE_URL}${path}`,
        }))
      ),
  },
  {
    name: "amzscout_open_page",
    description:
      "Open one of the main AMZScout pages in the current tab. Use amzscout_list_pages to get the available page ids.",
    inputSchema: {
      type: "object",
      properties: {
        page: {
          type: "string",
          description: "Page id, for example 'pro-ai' or 'pricing'.",
          enum: PAGES.map((page) => page.id),
        },
      },
      required: ["page"],
      additionalProperties: false,
    },
    execute: async ({ page }) => {
      const target = PAGES.find((item) => item.id === page);

      if (!target) {
        return asText({
          error: `Unknown page '${page}'`,
          available: PAGES.map((item) => item.id),
        });
      }

      window.location.assign(target.path);

      return asText({
        opened: `${SITE_URL}${target.path}`,
        title: target.title,
      });
    },
  },
  {
    name: "amzscout_get_ai_integration",
    description:
      "Get the connection details for the AMZScout MCP server and HTTP tools API: endpoints, transports, and how to authenticate.",
    inputSchema: {
      type: "object",
      properties: {},
      additionalProperties: false,
    },
    execute: async () => asText(AI_INTEGRATION),
  },
  {
    name: "amzscout_get_content_policy",
    description:
      "Get the AMZScout content usage policy for AI agents: what may be quoted, attribution requirements, support contact and policy links.",
    inputSchema: {
      type: "object",
      properties: {},
      additionalProperties: false,
    },
    execute: async () =>
      asText({
        llmsTxt: `${SITE_URL}/llms.txt`,
        contentSignals: "search=yes, ai-input=yes, ai-train=no",
        attribution:
          "Visibly credit AMZScout and link to the source page when quoting content.",
        support: "support@amzscout.net",
        termsOfUse: `${SITE_URL}/terms-of-use`,
        privacyPolicy: `${SITE_URL}/privacy-policy`,
      }),
  },
];

module.exports = function () {
  if (typeof navigator === "undefined" || !navigator.modelContext) return;

  try {
    // Chrome's origin trial build exposes registerTool(); the spec draft also
    // defines the declarative provideContext() form.
    if (typeof navigator.modelContext.registerTool === "function") {
      TOOLS.forEach((tool) => navigator.modelContext.registerTool(tool));
      return;
    }

    if (typeof navigator.modelContext.provideContext === "function") {
      navigator.modelContext.provideContext({ tools: TOOLS });
    }
  } catch (error) {
    // WebMCP is experimental — never let it break the page.
    console.warn("WebMCP registration skipped:", error);
  }
};
